import { Box, Grid, TextField } from '@mui/material';
import { SideDrawer, Typography } from '@peritasai/ui-components';
import { SubmitButton } from '../shared/SubmitButton';
import type { CaseTypeEntity } from '@peritasai/repository';
import { useCreateCaseType, useUpdateCaseType } from '@peritasai/repository';
import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { DrawerMode } from '../../utils/drawer';

export { DrawerMode as CaseTypeDrawerMode };

interface CaseTypeDrawerProps {
  open: boolean;
  mode: DrawerMode;
  selectedCaseType: CaseTypeEntity | null;
  onClose: () => void;
  onSuccess?: () => void;
}

interface FormState {
  caseTypeId: string;
  procedureCode: string;
  displayName: string;
  description: string;
  specialty: string;
}

interface FormErrors {
  caseTypeId?: string;
  procedureCode?: string;
  displayName?: string;
  apiError?: string;
}

const emptyForm: FormState = {
  caseTypeId: '',
  procedureCode: '',
  displayName: '',
  description: '',
  specialty: '',
};

function validate(
  form: FormState,
  mode: DrawerMode,
  t: (key: string, options?: Record<string, string>) => string
): FormErrors {
  const errors: FormErrors = {};
  if (mode === DrawerMode.Create && !form.caseTypeId.trim()) {
    errors.caseTypeId = t('pages.entityManagement.drawer.fieldRequired', {
      fieldName: t('pages.entityManagement.caseTypeDrawer.caseTypeIdLabel'),
    });
  }
  if (mode === DrawerMode.Create && !form.procedureCode.trim()) {
    errors.procedureCode = t('pages.entityManagement.drawer.fieldRequired', {
      fieldName: t('pages.entityManagement.caseTypeDrawer.procedureCodeLabel'),
    });
  }
  if (!form.displayName.trim()) {
    errors.displayName = t('pages.entityManagement.drawer.fieldRequired', {
      fieldName: t('pages.entityManagement.caseTypeDrawer.displayNameLabel'),
    });
  }
  return errors;
}

export const CaseTypeDrawer = ({
  open,
  mode,
  selectedCaseType,
  onClose,
  onSuccess,
}: CaseTypeDrawerProps) => {
  const { t } = useTranslation(['periverse']);
  const [form, setForm] = useState<FormState>(emptyForm);
  const [errors, setErrors] = useState<FormErrors>({});
  const [touched, setTouched] = useState(false);

  const createMutation = useCreateCaseType();
  const updateMutation = useUpdateCaseType();
  const isPending = createMutation.isPending || updateMutation.isPending;

  const entityName = t('pages.entityManagement.caseTypeDrawer.entityName');

  useEffect(() => {
    if (!open) return;
    if (mode === DrawerMode.Edit && selectedCaseType) {
      setForm({
        caseTypeId: selectedCaseType.caseTypeId ?? '',
        procedureCode: selectedCaseType.procedureCode ?? '',
        displayName: selectedCaseType.displayName ?? '',
        description: selectedCaseType.description ?? '',
        specialty: selectedCaseType.specialty ?? '',
      });
    } else {
      setForm(emptyForm);
    }
    setErrors({});
    setTouched(false);
  }, [open, mode, selectedCaseType]);

  const handleChange =
    (field: keyof FormState) => (e: React.ChangeEvent<HTMLInputElement>) => {
      const next = { ...form, [field]: e.target.value };
      setForm(next);
      if (touched) {
        setErrors(validate(next, mode, t));
      }
    };

  const handleSubmit = async () => {
    setTouched(true);
    const validationErrors = validate(form, mode, t);
    if (Object.keys(validationErrors).length > 0) {
      setErrors(validationErrors);
      return;
    }
    setErrors({});

    try {
      if (mode === DrawerMode.Create) {
        await createMutation.mutateAsync({
          caseTypeId: form.caseTypeId.trim(),
          procedureCode: form.procedureCode.trim(),
          displayName: form.displayName.trim(),
          ...(form.description.trim().length > 0 && { description: form.description.trim() }),
          ...(form.specialty.trim().length > 0 && { specialty: form.specialty.trim() }),
        });
        setForm(emptyForm);
        setTouched(false);
        onSuccess?.();
      } else if (mode === DrawerMode.Edit && selectedCaseType) {
        await updateMutation.mutateAsync({
          id: selectedCaseType.id,
          dto: {
            ...(form.procedureCode.trim().length > 0 && {
              procedureCode: form.procedureCode.trim(),
            }),
            displayName: form.displayName.trim(),
            description: form.description.trim() || null,
            specialty: form.specialty.trim() || null,
          },
        });
        onSuccess?.();
        onClose();
      }
    } catch (err) {
      const message =
        err instanceof Error
          ? err.message
          : t('pages.entityManagement.drawer.errorUnexpected');
      setErrors((prev) => ({ ...prev, apiError: message }));
    }
  };

  return (
    <SideDrawer
      open={open}
      title={
        mode === DrawerMode.Create
          ? t('pages.entityManagement.drawer.titleCreate', { entityName })
          : t('pages.entityManagement.drawer.titleEdit', { entityName })
      }
      onClose={onClose}
      footer={
        <SubmitButton disabled={isPending} onClick={() => void handleSubmit()}>
          {isPending
            ? t('pages.entityManagement.drawer.submitting')
            : t('pages.entityManagement.drawer.submit')}
        </SubmitButton>
      }
    >
      <Grid container spacing={2}>
        <Grid size={6}>
          <TextField
            label={`${t('pages.entityManagement.caseTypeDrawer.caseTypeIdLabel')} *`}
            fullWidth
            size="small"
            value={form.caseTypeId}
            onChange={handleChange('caseTypeId')}
            disabled={mode === DrawerMode.Edit}
            error={touched && !!errors.caseTypeId}
            helperText={touched ? errors.caseTypeId : undefined}
            slotProps={{ formHelperText: { sx: { ml: '5px' } } }}
          />
        </Grid>
        <Grid size={6}>
          <TextField
            label={`${t('pages.entityManagement.caseTypeDrawer.procedureCodeLabel')} *`}
            fullWidth
            size="small"
            value={form.procedureCode}
            onChange={handleChange('procedureCode')}
            disabled={mode === DrawerMode.Edit}
            error={touched && !!errors.procedureCode}
            helperText={touched ? errors.procedureCode : undefined}
            slotProps={{ formHelperText: { sx: { ml: '5px' } } }}
          />
        </Grid>

        <Grid size={6}>
          <TextField
            label={`${t('pages.entityManagement.caseTypeDrawer.displayNameLabel')} *`}
            fullWidth
            size="small"
            value={form.displayName}
            onChange={handleChange('displayName')}
            error={touched && !!errors.displayName}
            helperText={touched ? errors.displayName : undefined}
            slotProps={{ formHelperText: { sx: { ml: '5px' } } }}
          />
        </Grid>
        <Grid size={6}>
          <TextField
            label={t('pages.entityManagement.caseTypeDrawer.descriptionLabel')}
            fullWidth
            size="small"
            value={form.description}
            onChange={handleChange('description')}
          />
        </Grid>

        <Grid size={6}>
          <TextField
            label={t('pages.entityManagement.caseTypeDrawer.specialtyLabel')}
            fullWidth
            size="small"
            value={form.specialty}
            onChange={handleChange('specialty')}
          />
        </Grid>
      </Grid>

      {touched && errors.apiError && (
        <Box sx={{ mt: 2 }}>
          <Typography variant="body2" sx={{ color: 'error.main', mt: 0.5 }}>
            {errors.apiError}
          </Typography>
        </Box>
      )}
    </SideDrawer>
  );
};
